/**
 * StepEvaluation - Étape 1 : Évaluation du niveau sportif
 * Questionnaire une question par écran, style wizard
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { EVALUATION_QUESTIONS, SPORT_LEVELS } from '../../data/levels';
import { Confetti } from '../Confetti';

export function StepEvaluation({ onComplete, initialAnswers = {} }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  const [direction, setDirection] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const [resultLevel, setResultLevel] = useState(null);

  const questions = EVALUATION_QUESTIONS;
  const question = questions[currentQ];
  const totalQ = questions.length;

  const selectAnswer = useCallback((value) => {
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);

    if (question.type !== 'slider') {
      setTimeout(() => {
        if (currentQ < totalQ - 1) {
          setDirection(1);
          setCurrentQ(prev => prev + 1);
        } else {
          const level = onComplete(newAnswers);
          setResultLevel(level);
          setShowResult(true);
        }
      }, 300);
    }
  }, [answers, question, currentQ, totalQ, onComplete]);

  const goBack = useCallback(() => {
    if (currentQ > 0) {
      setDirection(-1);
      setCurrentQ(prev => prev - 1);
    }
  }, [currentQ]);

  const handleSliderSubmit = useCallback(() => {
    if (currentQ < totalQ - 1) {
      setDirection(1);
      setCurrentQ(prev => prev + 1);
    } else {
      const level = onComplete(answers);
      setResultLevel(level);
      setShowResult(true);
    }
  }, [currentQ, totalQ, answers, onComplete]);

  // Result screen
  if (showResult && resultLevel) {
    const levelInfo = SPORT_LEVELS.find(l => l.level === resultLevel);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center text-center px-4 py-8 space-y-6"
      >
        <Confetti active={true} />

        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2, stiffness: 200 }}
          className="text-7xl"
        >
          {levelInfo?.emoji}
        </motion.div>

        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-lg text-zinc-400"
          >
            Ton niveau sportif
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-5xl font-black text-transparent bg-clip-text
              bg-gradient-to-r from-orange-400 to-red-400 mt-2"
          >
            {resultLevel}/10
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-xl font-semibold text-white mt-2"
          >
            {levelInfo?.label}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="text-zinc-500 mt-1"
          >
            {levelInfo?.description}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-sm text-zinc-400 bg-zinc-800/60 rounded-xl p-4 border border-zinc-700/50"
        >
          Tes séances seront adaptées à ton niveau. Tu pourras toujours ajuster manuellement.
        </motion.div>
      </motion.div>
    );
  }

  // Question screen
  return (
    <div className="px-4 py-4">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {currentQ > 0 && (
          <button onClick={goBack} className="p-1 text-zinc-500 hover:text-white">
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <div className="flex-1 flex gap-1">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300
                ${i < currentQ ? 'bg-orange-500' : i === currentQ ? 'bg-orange-400' : 'bg-zinc-700'}`}
            />
          ))}
        </div>
        <span className="text-xs text-zinc-500 min-w-[40px] text-right">
          {currentQ + 1}/{totalQ}
        </span>
      </div>

      {/* Question */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentQ}
          custom={direction}
          initial={{ opacity: 0, x: direction * 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -direction * 50 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          <h3 className="text-xl font-bold text-white">{question.question}</h3>

          {question.type === 'slider' ? (
            <div className="space-y-6 mt-8">
              <div className="flex justify-between">
                {SPORT_LEVELS.map(l => (
                  <span key={l.level} className="text-lg">{l.level === (answers[question.id] || 5) ? l.emoji : ''}</span>
                ))}
              </div>

              <input
                type="range"
                min={question.min}
                max={question.max}
                value={answers[question.id] || 5}
                onChange={(e) => setAnswers(prev => ({ ...prev, [question.id]: parseInt(e.target.value) }))}
                className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:h-8 [&::-webkit-slider-thumb]:w-8
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-gradient-to-r
                  [&::-webkit-slider-thumb]:from-orange-500 [&::-webkit-slider-thumb]:to-red-500
                  [&::-webkit-slider-thumb]:shadow-lg"
              />

              <div className="flex justify-between text-xs text-zinc-500">
                <span>Débutant</span>
                <span>Coach/Pro</span>
              </div>

              <p className="text-center text-3xl font-black text-orange-400">
                {answers[question.id] || 5}/10
              </p>

              <button
                onClick={handleSliderSubmit}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-red-500
                  text-white font-bold flex items-center justify-center gap-2"
              >
                Continuer <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <div className="space-y-2 mt-4">
              {question.options.map((opt) => {
                const isSelected = answers[question.id] === opt.value;
                return (
                  <motion.button
                    key={opt.value}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => selectAnswer(opt.value)}
                    className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all
                      ${isSelected
                        ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                        : 'bg-zinc-800/60 border-zinc-700/50 text-zinc-300 hover:border-zinc-600'
                      }`}
                  >
                    {opt.label}
                  </motion.button>
                );
              })}
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
